const calculateLocationLedgerQuery = `
WITH
-- Latest full stock count before $2; serves as the ledger anchor.
lsc AS MATERIALIZED (
  SELECT stock_count_id, submitted_at, updated_at
  FROM avocado.data_stockcount
  WHERE fs_id = $1
    AND partial_count = false
    AND submitted_at <= $2
  ORDER BY submitted_at DESC
  LIMIT 1
),

-- Date boundaries for the ledger window; start_at falls back to epoch if no count exists.
bounds AS MATERIALIZED (
  SELECT
    COALESCE((SELECT submitted_at FROM lsc), '1970-01-01'::timestamptz) AS start_at,
    $2::timestamptz AS end_at
),

-- Single scan of stock count lines shared by both count_events and count_event_batches.
count_lines_raw AS MATERIALIZED (
  SELECT
    sc.submitted_at,
    sc.fs_id,
    sl.id    AS sl_id,
    sl.sku,
    sl.closing,
    COALESCE(sl.partner_balance, 0) - COALESCE(sl.partner_buyout, 0) AS partner_balance
  FROM avocado.data_stockcount sc
  JOIN avocado.data_stockcountline sl ON sl.stock_count_id = sc.id
  WHERE sc.fs_id = $1
    AND sc.submitted_at >= (SELECT start_at FROM bounds)
    AND sc.submitted_at <= (SELECT end_at FROM bounds)
),

count_events AS (
  SELECT sku, closing, partner_balance, submitted_at AS event_date, fs_id
  FROM count_lines_raw
),

-- Batch breakdown of count events; derived from count_lines_raw to avoid a second scan.
count_event_batches AS (
  SELECT clr.sku, d.batch, d.amount, clr.submitted_at AS event_date, clr.fs_id
  FROM count_lines_raw clr
  JOIN avocado.data_stockcountline_details d ON d.stock_count_line = clr.sl_id
),

-- Earliest sent snapshot per shipment; aggregated at shipment level to avoid per-line lookups.
origin_snap AS MATERIALIZED (
  SELECT ds.shipment_id, MIN(dss.created_at) AS snap_at
  FROM avocado.data_shipment ds
  JOIN avocado.data_shipmentsnapshot dss
    ON dss.shipment_id = ds.shipment_id AND dss.status = 'sent'
  WHERE ds.origin_id = $1
    AND ds.status IN ('sent', 'arrived', 'received')
  GROUP BY ds.shipment_id
),

-- Outbound shipments from this location; falls back to created_at if no snapshot exists.
shipments_sent AS MATERIALIZED (
  SELECT
    dsl.origin_sku                      AS sku,
    -dsl.amount                         AS closing,
    0                                   AS partner_balance,
    COALESCE(os.snap_at, ds.created_at) AS event_date,
    ds.origin_id                        AS fs_id,
    ds.shipment_id,
    dsl.id                              AS sl_id
  FROM avocado.data_shipment ds
  JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
  LEFT JOIN origin_snap os ON os.shipment_id = ds.shipment_id
  WHERE ds.origin_id = $1
    AND ds.status IN ('sent', 'arrived', 'received')
    AND COALESCE(os.snap_at, ds.created_at) > (SELECT start_at FROM bounds)
    AND COALESCE(os.snap_at, ds.created_at) <= (SELECT end_at FROM bounds)
),

-- Inbound shipments to this location.
shipments_received AS MATERIALIZED (
  SELECT
    dsl.destination_sku     AS sku,
    dsl.amount              AS closing,
    0                       AS partner_balance,
    ds.updated_at           AS event_date,
    ds.destination_id       AS fs_id,
    ds.shipment_id,
    dsl.id                  AS sl_id
  FROM avocado.data_shipment ds
  JOIN avocado.data_shipmentline dsl ON dsl.shipment_id = ds.id
  WHERE ds.destination_id = $1
    AND ds.status = 'received'
    AND ds.updated_at > (SELECT start_at FROM bounds)
    AND ds.updated_at <= (SELECT end_at FROM bounds)
),

ledger_shipments AS (
  SELECT sku, closing, partner_balance, event_date, fs_id, shipment_id
  FROM shipments_sent
  UNION ALL
  SELECT sku, closing, partner_balance, event_date, fs_id, shipment_id
  FROM shipments_received
),

-- Batch breakdowns derived from materialised shipments; no re-scan of shipment tables.
shipments_sent_batches AS (
  SELECT ss.sku, dd.batch, -dd.amount AS amount, ss.event_date, ss.fs_id, ss.shipment_id
  FROM shipments_sent ss
  JOIN avocado.data_shipmentline_details dd ON dd.shipment_line = ss.sl_id
),

shipments_received_batches AS (
  SELECT sr.sku, dd.batch, dd.amount, sr.event_date, sr.fs_id, sr.shipment_id
  FROM shipments_received sr
  JOIN avocado.data_shipmentline_details dd ON dd.shipment_line = sr.sl_id
),

ledger_shipments_batches AS (
  SELECT * FROM shipments_sent_batches
  UNION ALL
  SELECT * FROM shipments_received_batches
),

-- All stock events tagged; window MAX carries the last count date forward to assign each shipment to its epoch.
combined_events AS MATERIALIZED (
  SELECT sku, event_date, closing, partner_balance, fs_id, true  AS is_count FROM count_events
  UNION ALL
  SELECT sku, event_date, closing, partner_balance, fs_id, false AS is_count FROM ledger_shipments
),

stock_activity AS (
  SELECT
    sku, event_date, closing, partner_balance, fs_id,
    CASE
      WHEN is_count THEN event_date
      ELSE MAX(CASE WHEN is_count THEN event_date END) OVER (
             PARTITION BY sku
             ORDER BY event_date, is_count   -- false(0) before true(1): shipments precede counts at equal timestamps
             ROWS BETWEEN UNBOUNDED PRECEDING AND 1 PRECEDING
           )
    END AS partition_window
  FROM combined_events
),

combined_batch_events AS MATERIALIZED (
  SELECT sku, batch, event_date, amount, fs_id, true  AS is_count FROM count_event_batches
  UNION ALL
  SELECT sku, batch, event_date, amount, fs_id, false AS is_count FROM ledger_shipments_batches
),

-- Epoch assignment for batches; partitioned by sku only since all batches share the same count boundary.
batch_activity AS (
  SELECT
    sku, batch, event_date, amount, fs_id,
    CASE
      WHEN is_count THEN event_date
      ELSE MAX(CASE WHEN is_count THEN event_date END) OVER (
             PARTITION BY sku
             ORDER BY event_date, is_count
             ROWS BETWEEN UNBOUNDED PRECEDING AND 1 PRECEDING
           )
    END AS partition_window
  FROM combined_batch_events
),

-- Running balance per SKU; picks the latest row per SKU as the current stock level.
final AS (
  SELECT sku, event_date, fs_id, sl_balance, partner_balance
  FROM (
    SELECT
      sku, event_date, fs_id,
      SUM(closing)         OVER w AS sl_balance,
      SUM(partner_balance) OVER w AS partner_balance,
      ROW_NUMBER()         OVER (PARTITION BY sku ORDER BY event_date DESC) AS rn
    FROM stock_activity
    WINDOW w AS (PARTITION BY partition_window, sku ORDER BY event_date)
  ) sub
  WHERE rn = 1
),

final_batches AS (
  SELECT sku, batch, event_date, fs_id, amount
  FROM (
    SELECT
      sku, batch, event_date, fs_id,
      SUM(amount) OVER w  AS amount,
      ROW_NUMBER() OVER (PARTITION BY sku, batch ORDER BY event_date DESC) AS rn
    FROM batch_activity
    WINDOW w AS (PARTITION BY partition_window, sku, batch ORDER BY event_date)
  ) sub
  WHERE rn = 1
),

ledgers AS (
  SELECT
    f.sku,
    f.event_date AS date,
    f.sl_balance,
    f.partner_balance,
    f.fs_id,
    dp.display_name AS product,
    l.uuid          AS location_id
  FROM final f
  JOIN avocado.data_productsku dp ON dp.old_sku_id = f.sku
  JOIN avocado.data_location l    ON l.fsid = f.fs_id
),

ledgers_batches AS (
  SELECT
    fb.sku,
    fb.batch,
    fb.event_date AS date,
    fb.amount,
    fb.fs_id,
    l.uuid AS location_id
  FROM final_batches fb
  JOIN avocado.data_location l ON l.fsid = fb.fs_id
),

-- Batch JSON pre-aggregated per (location, sku) to avoid inline aggregation in the final SELECT.
batch_agg AS (
  SELECT
    location_id,
    sku,
    JSON_AGG(JSON_BUILD_OBJECT('batch', batch, 'amount', amount) ORDER BY batch) AS batches
  FROM ledgers_batches
  GROUP BY location_id, sku
),

shipment_ids AS (
  SELECT DISTINCT shipment_id FROM ledger_shipments
)

SELECT
  base.location_id,
  base.location_fsid,
  (SELECT stock_count_id FROM lsc)                 AS base_report,
  (SELECT updated_at       FROM lsc)               AS base_report_updated_at,
  (SELECT JSON_AGG(shipment_id) FROM shipment_ids) AS added_shipments,
  base.updated_at,
  base.stock
FROM (
  SELECT
    l.location_id,
    dl.fsid AS location_fsid,
    MAX(l.date) AS updated_at,
    JSON_AGG(
      JSON_BUILD_OBJECT(
        'sku',             l.sku,
        'amount',          l.sl_balance,
        'partner_balance', l.partner_balance,
        'batches',         COALESCE(ba.batches, '[]'::json)
      )
      ORDER BY l.sku
    ) AS stock
  FROM ledgers l
  LEFT JOIN batch_agg ba
    ON ba.location_id = l.location_id AND ba.sku = l.sku
  JOIN avocado.data_location dl
    ON dl.uuid = l.location_id
  JOIN avocado.data_productsku ps
    ON ps.old_sku_id = l.sku
   AND NOT ps._deleted
   AND ps.market_id = dl.market_id
  GROUP BY l.location_id, dl.fsid
) AS base
`

module.exports = calculateLocationLedgerQuery
